import { useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Alert, Spinner } from "@vlab-corporation/design-system";
import { AppLayout } from "@/components/AppLayout";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { usePendingJournals, getMonthRange } from "@/hooks/usePendingJournals";
import { resolveMyStaffKey } from "@/utils/staff-identity";
import type { AuthUser } from "@/types/auth";

function getCurrentYearMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** "YYYY-MM" → "YYYY년 MM월" */
function formatYearMonth(ym: string): string {
  const [y, m] = ym.split("-");
  return `${y}년 ${Number(m)}월`;
}

function getPrevYearMonth(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  const prev = new Date(y, m - 2, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
}

function getNextYearMonth(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  const next = new Date(y, m, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`;
}

export function DashboardPage() {
  const user = useAuthenticatedUser();
  if (!user) return null;
  return <DashboardPageContent user={user} />;
}

function DashboardPageContent({ user }: { user: AuthUser }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentYm = getCurrentYearMonth();
  const yearMonth = searchParams.get("month") ?? currentYm;
  const isCurrentMonth = yearMonth === currentYm;

  const goToPrev = useCallback(() => {
    const prev = getPrevYearMonth(yearMonth);
    setSearchParams({ month: prev }, { replace: true });
  }, [yearMonth, setSearchParams]);

  const goToNext = useCallback(() => {
    const next = getNextYearMonth(yearMonth);
    if (next > currentYm) return;
    // 이번 달이면 month param 제거 (깔끔한 URL)
    if (next === currentYm) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ month: next }, { replace: true });
    }
  }, [yearMonth, currentYm, setSearchParams]);

  const { centerOwnerUid, centerId } = user.activeMembership;
  const myStaffKey = resolveMyStaffKey(user.activeMembership);

  const monthRange = useMemo(() => getMonthRange(yearMonth), [yearMonth]);

  const {
    totalCount,
    completedCount,
    pendingCount,
    isLoading,
    error,
    apiError,
    journalWritingStartDate,
  } = usePendingJournals(centerOwnerUid, centerId, myStaffKey, monthRange);

  // 이전달 제한: journalWritingStartDate의 월보다 이전으로 갈 수 없음
  const canGoPrev = (() => {
    if (!journalWritingStartDate) return true;
    const minYm = journalWritingStartDate.substring(0, 7);
    return yearMonth > minYm;
  })();

  return (
    <AppLayout>
      <h2 className="text-xl font-semibold mb-1">대시보드</h2>

      {/* 월 네비게이션 */}
      <div className="flex items-center justify-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={goToPrev} disabled={!canGoPrev} className="px-2">
          ‹ 이전달
        </Button>
        <span className="text-sm font-medium text-gray-700 min-w-[100px] text-center">
          {formatYearMonth(yearMonth)}
        </span>
        {!isCurrentMonth && (
          <Button variant="ghost" size="sm" onClick={goToNext} className="px-2">
            다음달 ›
          </Button>
        )}
        {isCurrentMonth && <span className="w-[72px]" />}
      </div>

      {/* Studiomate API error */}
      {apiError && (
        <Alert variant="warning" className="mb-4">
          {apiError.message}
        </Alert>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <Alert variant="error" className="mb-4">
          수업 정보를 불러오지 못했습니다.
        </Alert>
      ) : (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Card className="p-3 text-center">
              <p className="text-sm text-gray-500">전체 수업</p>
              <p className="text-2xl font-bold">{totalCount}</p>
            </Card>
            <Card
              className="p-3 text-center cursor-pointer hover:bg-gray-50"
              onClick={() =>
                completedCount > 0 && navigate(`/completed-journals?month=${yearMonth}`)
              }
            >
              <p className="text-sm text-gray-500">일지 완료</p>
              <p className="text-2xl font-bold text-green-600">{completedCount}</p>
            </Card>
            <Card
              className="p-3 text-center cursor-pointer hover:bg-gray-50"
              onClick={() => pendingCount > 0 && navigate(`/pending-journals?month=${yearMonth}`)}
            >
              <p className="text-sm text-gray-500">미작성</p>
              <p className="text-2xl font-bold text-red-500">{pendingCount}</p>
            </Card>
          </div>

          {/* Pending journal alert */}
          {pendingCount > 0 && (
            <Alert variant="warning" className="mb-4">
              일지 미작성 수업이 {pendingCount}건 있습니다. 일지를 작성해야 정산에 반영됩니다.
            </Alert>
          )}

          {totalCount === 0 && !apiError && (
            <Card className="p-4 text-center text-gray-500">
              {formatYearMonth(yearMonth)} 수업이 없습니다.
            </Card>
          )}
        </>
      )}

      {/* Quick actions */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <Button variant="outline" onClick={() => navigate("/classes")} className="w-full">
          수업 목록
        </Button>
        <Button variant="outline" onClick={() => navigate("/members")} className="w-full">
          회원 목록
        </Button>
        <Button variant="outline" onClick={() => navigate("/settlement")} className="w-full">
          정산 현황
        </Button>
        <Button variant="outline" onClick={() => navigate("/ticket-catalog")} className="w-full">
          수강권 관리
        </Button>
      </div>

      {/* KPIs */}
      <div className="mt-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">KPIs</h3>
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-3 text-center">
            <p className="text-xs text-gray-500">총 재등록률</p>
            <p className="text-xl font-bold text-gray-300">—</p>
            <p className="text-xs text-gray-400 mt-1">추후 제공 예정</p>
          </Card>
          <Card className="p-3 text-center">
            <p className="text-xs text-gray-500">총 OT 성공률</p>
            <p className="text-xl font-bold text-gray-300">—</p>
            <p className="text-xs text-gray-400 mt-1">추후 제공 예정</p>
          </Card>
          <Card className="p-3 text-center">
            <p className="text-xs text-gray-500">최근 10회 OT 성공률</p>
            <p className="text-xl font-bold text-gray-300">—</p>
            <p className="text-xs text-gray-400 mt-1">추후 제공 예정</p>
          </Card>
          <Card className="p-3 text-center">
            <p className="text-xs text-gray-500">최근 10회 재등록 성공률</p>
            <p className="text-xl font-bold text-gray-300">—</p>
            <p className="text-xs text-gray-400 mt-1">추후 제공 예정</p>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
